import React, { useMemo } from 'react';
import SiteCharts from '../../../components/SiteCharts';
import { maximumEndcCellChartConfig } from '../cellChartConfigs';

/**
 * Props for the MaximumEndcCell component
 */
interface MaximumEndcCellProps {
  /** Cell identifier passed to API as cell_name parameter */
  cellName: string;
  /** Callback function to toggle fullscreen mode */
  onFullscreenToggle: () => void;
  /** Current fullscreen state */
  isFullscreen: boolean;
}

/**
 * Maximum ENDC Users Chart (LTE Cell Level)
 * 
 * Displays the maximum number of E-UTRA-NR Dual Connectivity (ENDC) users for individual LTE cells.
 * Shows the peak number of users simultaneously connected in dual connectivity mode (LTE + NR).
 * 
 * This component is a thin wrapper around the SiteCharts template component,
 * configured with cell-level maximum ENDC metrics from cellChartConfigs.ts
 * 
 * @component
 * @example
 * ```tsx
 * <MaximumEndcCell
 *   cellName="173716_145"
 *   onFullscreenToggle={() => setFullscreen(!fullscreen)}
 *   isFullscreen={false}
 * />
 * ```
 * 
 * @see {@link ../cellChartConfigs.ts} - Chart configuration (maximumEndcCellChartConfig)
 * @see {@link ../../../components/SiteCharts.tsx} - Template component
 * @see API: /api/lte-cell/maximum-endc?cell_name={cellName}
 */

/**
 * Maximum ENDC Connected UE Sessions Chart (Cell Level)
 * 
 * Displays maximum ENDC (E-UTRA-NR Dual Connectivity) connected UE sessions
 * for a specific LTE cell. ENDC allows UEs to connect to both LTE and 5G NR simultaneously.
 */
const MaximumEndcCell: React.FC<MaximumEndcCellProps> = ({ 
  cellName, 
  onFullscreenToggle, 
  isFullscreen 
}) => {
  // Memoize additionalParams to prevent infinite re-renders
  const additionalParams = useMemo(() => ({ cell_name: cellName }), [cellName]);

  return (
    <SiteCharts 
      config={maximumEndcCellChartConfig}
      onFullscreenToggle={onFullscreenToggle}
      isFullscreen={isFullscreen}
      additionalParams={additionalParams}
    />
  );
};

export default MaximumEndcCell;
